import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, ShieldAlert } from "lucide-react";
import {
  pricingApi,
  CHANNEL_LABEL,
  D3B_THRESHOLD_PCT,
  type Channel,
  type ChannelUpdateResult,
} from "@/services/pricing";
import { Can } from "@/auth/permissions";
import { PageShell, Pill, LoadingState } from "@/shared/ui/PageHeader";
import { MetricCard } from "@/shared/ui/Card";

/**
 * W4 · 4 渠道详情共用模板（API/B2B/社媒/客户端）
 *  - markup + 含税策略 + 含费策略 + 更新频率
 *  - delta > 30% 触发 D3B 老板审
 */
const VALID_CHANNELS: Channel[] = ["api", "b2b", "social", "direct"];

export function ChannelPricingDetailPage() {
  const { channel } = useParams<{ channel: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  if (!channel || !VALID_CHANNELS.includes(channel as Channel)) {
    return (
      <PageShell title="渠道不存在" subtitle="无效的渠道 slug">
        <button
          onClick={() => navigate("/admin/pricing")}
          className="flex h-9 w-fit items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> 返回定价中心
        </button>
      </PageShell>
    );
  }

  const ch = channel as Channel;
  const channels = useQuery({
    queryKey: ["pricing", "channels"],
    queryFn: () => pricingApi.listChannels(),
  });
  const current = channels.data?.find((m) => m.channel === ch);

  const [markup, setMarkup] = useState<number>(0);
  const [taxIncluded, setTaxIncluded] = useState(true);
  const [feeIncluded, setFeeIncluded] = useState(false);
  const [reason, setReason] = useState("调整 markup");
  const [result, setResult] = useState<ChannelUpdateResult | null>(null);

  useEffect(() => {
    if (current) {
      setMarkup(current.markup_pct);
      setTaxIncluded(current.tax_included);
      setFeeIncluded(current.fee_included);
    }
  }, [current?.markup_pct, current?.tax_included, current?.fee_included]);

  const save = useMutation({
    mutationFn: () =>
      pricingApi.updateChannel(ch, {
        markup_pct: markup,
        reason,
        tax_included: taxIncluded,
        fee_included: feeIncluded,
      }),
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ["pricing"] });
    },
  });

  const delta = current ? Math.abs(markup - current.markup_pct) : 0;
  const willTriggerD3B = delta > D3B_THRESHOLD_PCT;

  if (channels.isLoading) return <LoadingState />;
  if (channels.isError)
    return (
      <PageShell title={CHANNEL_LABEL[ch]} subtitle="加载失败">
        <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500"><span>渠道配置加载失败</span><button onClick={() => channels.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button></div>
      </PageShell>
    );
  if (!current)
    return (
      <PageShell title={CHANNEL_LABEL[ch]} subtitle="渠道未找到">
        <div className="py-16 text-center text-[13px] text-slate-500">渠道未找到</div>
      </PageShell>
    );

  return (
    <PageShell
      title={CHANNEL_LABEL[ch]}
      subtitle={`渠道 slug: ${ch} · ${current.channel_label}`}
      actions={
        <button
          onClick={() => navigate("/admin/pricing")}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[13px] font-medium hover:bg-slate-50"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> 返回
        </button>
      }
    >
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
        <MetricCard label="当前 markup" value={`+${current.markup_pct}%`} />
        <MetricCard label="含税" value={current.tax_included ? "是" : "否"} />
        <MetricCard label="含费" value={current.fee_included ? "是" : "否"} />
        <MetricCard
          label="待审变更"
          value={current.pending_change_id ? "1 笔" : "无"}
          hint={current.pending_change_id ? "老板审批中" : undefined}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-base font-semibold text-slate-900">编辑渠道参数</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Markup 百分比">
            <input
              type="number"
              step="0.1"
              value={markup}
              onChange={(e) => setMarkup(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
          <Field label="变更理由">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
          <Field label="含税策略">
            <label className="flex h-9 items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={taxIncluded}
                onChange={(e) => setTaxIncluded(e.target.checked)}
              />
              <span>含税（含 sales tax / VAT）</span>
            </label>
          </Field>
          <Field label="含费策略">
            <label className="flex h-9 items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={feeIncluded}
                onChange={(e) => setFeeIncluded(e.target.checked)}
              />
              <span>含费（含手续费 / 平台费）</span>
            </label>
          </Field>
        </div>

        {willTriggerD3B && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-800">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <strong>D3B 老板审：</strong>
              当前 delta = {delta.toFixed(1)}% &gt; {D3B_THRESHOLD_PCT}% 阈值 ·
              提交后不立即生效 · 进入老板审批队列。
            </div>
          </div>
        )}

        <Can perm="pricing.channel_markups.update">
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending || !!current.pending_change_id}
            className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {willTriggerD3B ? "提交审批" : "立即生效"}
          </button>
          {current.pending_change_id && (
            <Pill tone="warning">已有待审变更 · 不能再次提交</Pill>
          )}
        </div>
        </Can>

        {save.isError && (
          <div className="mt-3 text-[12px] text-rose-600">
            保存失败：{(save.error as Error).message}
          </div>
        )}

        {result && (
          <div
            className={
              "mt-4 rounded-lg border p-3 text-[12px] " +
              (result.applied
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-300 bg-amber-50 text-amber-800")
            }
          >
            {result.applied ? (
              <>
                ✅ Markup 已生效：+{result.new_markup_pct}%（delta {result.delta_pct.toFixed(1)}%）
              </>
            ) : (
              <>
                🟡 已提交老板审批（delta {result.delta_pct.toFixed(1)}% &gt; {D3B_THRESHOLD_PCT}% 阈值）·
                change_request_id = {result.change_request_id?.slice(0, 8)}…
              </>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}
